import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MapPin, ClipboardList, Users } from "lucide-react";
import { useApi, errMsg, fileUrl, money, specLabel, fmtDate } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PageTitle, PhotoPicker, StatusPill, Empty } from "@/components/bits";

export function JobCard({ j, extra }) {
  return (
    <Link to={`/chamados/${j.id}`} data-testid={`job-card-${j.id}`} className="panel panel-hover flex min-w-0 gap-4 p-4">
      {j.photos?.[0] ? <img src={fileUrl(j.photos[0])} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" /> :
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-white/5"><ClipboardList className="text-slate-500" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><StatusPill s={j.status} /><span className="text-xs text-slate-400">{specLabel(j.specialty, j.country)}</span></div>
        <h3 className="mt-1 truncate text-xl font-bold">{j.title}</h3>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
          <span><MapPin size={12} className="mr-1 inline" />{j.district ? `${j.district}, ` : ""}{j.city}{j.distance_km != null ? ` · ${j.distance_km} km` : ""}</span>
          {j.proposal_count !== undefined && <span><Users size={12} className="mr-1 inline" />{j.proposal_count} proposta(s)</span>}
          {j.agreed_price && <span className="font-bold text-[#FFC700]">{money(j.agreed_price, j.country)}</span>}
          <span>{fmtDate(j.created_at)}</span>
        </div>
        {extra}
      </div>
    </Link>
  );
}

export function MyJobs() {
  const api = useApi();
  const { me } = useMe();
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState("all");
  useEffect(() => { api.get("/jobs/mine").then((r) => setJobs(r.data)).catch(() => setJobs([])); }, [api]);
  const isPro = me?.profile_type === "pro";
  const list = (jobs || []).filter((j) => tab === "all" || j.status === tab);
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow={isPro ? "Serviços aceitos" : "Seus pedidos"} title={isPro ? "Meus serviços" : "Chamados"}>
        {!isPro && <Link to="/chamados/novo" data-testid="new-job-btn" className="btn-hazard"><Plus size={18} />Novo chamado</Link>}
      </PageTitle>
      <div className="mb-4 flex flex-wrap gap-2">
        {[["all", "Todos"], ["open", "Abertos"], ["assigned", "Em andamento"], ["completed", "Concluídos"]].map(([k, l]) => (
          <button key={k} data-testid={`jobs-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#FFC700] text-[#111317]" : "border border-white/15 text-slate-300"}`}>{l}</button>
        ))}
      </div>
      {jobs && !list.length ? (
        <Empty icon={ClipboardList} title="Nada por aqui" text={isPro ? "Quando um cliente aceitar sua proposta, o serviço aparece aqui." : "Publique um chamado ou comece pelo orçamento por foto."}>
          <Link to={isPro ? "/mural" : "/diagnostico"} className="btn-hazard" data-testid="jobs-empty-cta">{isPro ? "Ver mural" : "Orçamento por foto"}</Link>
        </Empty>
      ) : <div className="grid gap-3">{list.map((j) => <JobCard key={j.id} j={j} />)}</div>}
    </div>
  );
}

export function NewJob() {
  const api = useApi();
  const nav = useNavigate();
  const { state } = useLocation();
  const { me, meta } = useMe();
  const specs = meta?.specialties || [];
  const [f, setF] = useState({ title: state?.title || "", description: state?.description || "", specialty: specs.includes(state?.specialty) ? state.specialty : (state?.specialty && specs.find((s) => state.specialty.includes(s))) || "", photos: state?.photos || [], address: me?.address || "" });
  const [busy, setBusy] = useState(false);
  if (me?.profile_type === "pro") return <div className="p-8 text-center text-slate-400">Apenas clientes publicam chamados.</div>;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/jobs", { ...f, diagnosis_id: state?.diagnosis_id }); toast.success("Chamado publicado! Profissionais da região serão avisados."); nav(`/chamados/${data.id}`, { replace: true }); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow="Novo chamado" title="O que precisa ser feito?" />
      <form onSubmit={submit} className="panel space-y-5 p-6">
        {state?.diagnosis_id && <div className="rounded-xl border border-[#FFC700]/30 bg-[#FFC700]/10 px-4 py-2 text-sm text-[#FFC700]" data-testid="newjob-diag-attached">Diagnóstico da obra anexado ao chamado</div>}
        <label className="block"><span className="label">Título</span><input data-testid="newjob-title" className="field" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ex.: Trocar torneira da cozinha" required /></label>
        <label className="block"><span className="label">Descrição</span><textarea data-testid="newjob-description" className="field min-h-[120px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required /></label>
        <div><span className="label">Especialidade</span>
          <div className="flex flex-wrap gap-2">{specs.map((s) => (
            <button key={s} type="button" data-testid={`newjob-spec-${s}`} onClick={() => setF({ ...f, specialty: s })} className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${f.specialty === s ? "border-[#FFC700] bg-[#FFC700] text-[#111317]" : "border-white/15 text-slate-300"}`}>{specLabel(s, me?.country)}</button>
          ))}</div>
        </div>
        <div><span className="label">Fotos</span><PhotoPicker testid="newjob-photo" value={f.photos} onChange={(v) => setF((o) => ({ ...o, photos: v }))} max={6} /></div>
        <label className="block"><span className="label">Endereço do serviço</span><input data-testid="newjob-address" className="field" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /><span className="mt-1 block text-xs text-slate-500">Só o profissional contratado vê o endereço completo. Os demais veem apenas {me?.district}, {me?.city}.</span></label>
        <button data-testid="newjob-submit" className="btn-hazard w-full py-4 text-lg" disabled={busy || !f.specialty}>{busy ? "Publicando…" : "Publicar chamado"}</button>
      </form>
    </div>
  );
}
